import type { ReactNode } from 'react';

// Two-panel logged-out shell, ported from the admin-web login screen
// (apps/admin-web/src/routes/login.tsx) so signup.groupfire.com matches
// app.groupfire.com. signup-web has no shared Icon component, so the four
// feature icons are inlined lucide paths.

const FEATURES = [
  {
    label: 'Events, RSVPs & check-in',
    // lucide "calendar"
    path: (
      <>
        <path d="M8 2v4" />
        <path d="M16 2v4" />
        <rect width="18" height="18" x="3" y="4" rx="2" />
        <path d="M3 10h18" />
      </>
    ),
  },
  {
    label: 'Member directory & profiles',
    // lucide "users"
    path: (
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    ),
  },
  {
    label: 'Announcements & SMS',
    // lucide "megaphone"
    path: (
      <>
        <path d="m3 11 18-5v12L3 14v-3z" />
        <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
      </>
    ),
  },
  {
    label: 'Dues & payments',
    // lucide "credit-card"
    path: (
      <>
        <rect width="20" height="14" x="2" y="5" rx="2" />
        <line x1="2" x2="22" y1="10" y2="10" />
      </>
    ),
  },
] as const;

export function BrandShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen md:grid md:grid-cols-2">
      {/* LEFT brand panel — hidden on mobile */}
      <aside className="hidden md:flex flex-col justify-between bg-gradient-to-br from-brand to-brand-dark p-12 text-white">
        {/* Wordmark */}
        <div className="flex items-center gap-2.5">
          <div className="relative size-7 flex-shrink-0 rounded-lg bg-white">
            <div
              className="absolute inset-[6px] rounded-sm bg-brand"
              style={{ clipPath: 'polygon(50% 0%,100% 38%,82% 100%,18% 100%,0 38%)' }}
            />
          </div>
          <span className="text-lg font-bold tracking-tight">GroupFire</span>
        </div>

        {/* Hero copy + feature rows */}
        <div>
          <h2 className="text-3xl font-bold leading-tight tracking-tight">
            Everything your community needs, in one place.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-white/80">
            The platform powering member organizations — from events and dues to a directory and
            group messaging.
          </p>
          <ul className="mt-6 space-y-3.5">
            {FEATURES.map(({ label, path }) => (
              <li key={label} className="flex items-center gap-3 text-sm font-medium">
                <span className="grid size-8 flex-shrink-0 place-items-center rounded-lg bg-white/15">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className="size-4 text-white"
                  >
                    {path}
                  </svg>
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>

        {/* Quote */}
        <blockquote className="border-l-2 border-white/40 pl-3.5 text-sm leading-relaxed text-white/75">
          &ldquo;Our chapter runs entirely on GroupFire — members actually use it.&rdquo;
          <br />
          <span className="mt-1 block opacity-75">— YPO Chapter Administrator</span>
        </blockquote>
      </aside>

      {/* RIGHT — content card, centered on cream */}
      <main className="grid min-w-0 grid-cols-[minmax(0,1fr)] place-items-center bg-cream p-6">
        <div className="w-full min-w-0 max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          {children}
        </div>
      </main>
    </div>
  );
}
